import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAnonId,
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import { multiplierAt } from "@karrtopelka/game-api";
import { fromScoreValue } from "@karrtopelka/game-api/crash";
import { colors } from "@karrtopelka/ui";
import type { CardProps } from "@karrtopelka/cards/types";

import { buildRocketCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { useRocketStore, type RocketState } from "./store.js";

type Board = ReturnType<typeof useLeaderboard>;

/** Sibling games for the desktop "more games" strip. */
const SIBLINGS = [
  { name: "draw", hook: "the daily number" },
  { name: "crash", hook: "solo climb" },
  { name: "plinko", hook: "one ball, 470x" },
] as const;

export function App() {
  useEffect(() => trackPageView("rocket"), []);

  // One leaderboard instance feeds BOTH the desktop rail and the mobile summary
  // board — no double fetching.
  const board = useLeaderboard("rocket");
  const [handle, setHandle] = useState<string | null>(() => getHandle());
  const state = useRocketStore((s) => s.state);

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:content-start lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3 lg:justify-self-center">
        <h1 className="font-display text-3xl font-extrabold tracking-tight lg:text-5xl">
          rocket
          <span className="text-accent lg:drop-shadow-[0_0_14px_rgba(255,111,165,0.7)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:mt-1">
          one rocket every 10 minutes. everyone rides the same one.
        </p>
      </header>

      {/* LEFT rail — desktop only: mission briefing + this flight. Quiet furniture. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-4"
        style={{ animationDelay: "120ms" }}
      >
        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            how it works
          </h2>
          <ul className="mt-2 space-y-1.5 text-sm text-text-faint">
            <li>one shared rocket, every 10 minutes.</li>
            <li>board before launch. cash out mid-climb.</li>
            <li>hold too long, everyone sees you burn.</li>
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur transition-all duration-200 hover:border-accent">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            this flight
          </h2>
          <span className="mt-1 block font-display text-3xl font-extrabold tabular-nums text-accent">
            {state ? state.boardedCount : "—"}
          </span>
          <p className="text-xs text-text-faint">
            aboard{state?.meBoarded ? " — you're one of them" : ""}
          </p>
          {state?.last && (
            <p className="mt-2 border-t border-border pt-2 text-xs text-text-faint">
              last one blew at{" "}
              <span className="font-mono text-danger">
                {state.last.crashPoint.toFixed(2)}x
              </span>
            </p>
          )}
        </div>

        <div className="rounded-lg border border-border bg-surface/60 p-4 text-sm text-text-faint backdrop-blur">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-text">
            space
          </kbd>{" "}
          cashes out mid-flight
        </div>
      </aside>

      <Game board={board} handle={handle} onHandle={setHandle} />

      {/* RIGHT rail — desktop only: the survivors' wall, live in every phase. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3"
        style={{ animationDelay: "200ms" }}
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            best shared flights
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={handle}
            formatValue={(v) => `${fromScoreValue(v)}x`}
            emptyLabel="no survivors yet — next flight in 10."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-6 lg:justify-self-center"
        style={{ animationDelay: "240ms" }}
      >
        {/* mobile: tiny cross-links, as ever */}
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://draw.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try draw — the daily number, 19:00 UTC →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>

        {/* desktop: more games strip */}
        <div className="hidden items-center gap-3 lg:flex">
          {SIBLINGS.map((g) => (
            <a
              key={g.name}
              href={`https://${g.name}.karrtopelka.com`}
              className="group flex flex-col items-center rounded-lg border border-border bg-surface/60 px-5 py-2.5 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
            >
              <span className="font-display text-sm font-bold text-text transition-colors duration-200 group-hover:text-accent">
                {g.name}.
              </span>
              <span className="text-[11px] text-text-faint">{g.hook}</span>
            </a>
          ))}
          <a
            href="https://karrtopelka.com"
            className="ml-2 transition-colors duration-200 hover:text-accent"
          >
            all games →
          </a>
        </div>
      </footer>
    </main>
  );
}

function fmtMMSS(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function Game({
  board,
  handle,
  onHandle,
}: {
  board: Board;
  handle: string | null;
  onHandle: (h: string) => void;
}) {
  const state = useRocketStore((s) => s.state);
  const offset = useRocketStore((s) => s.clockOffset);
  const cashed = useRocketStore((s) => s.cashed);
  const apply = useRocketStore((s) => s.apply);
  const setCashed = useRocketStore((s) => s.setCashed);

  const [showHandle, setShowHandle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/rocket/state?anonId=${getAnonId()}`);
      if (!res.ok) return;
      apply((await res.json()) as RocketState);
    } catch {
      /* poll again later */
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Corrected wall clock, ticking 4×/s — drives countdowns and phase flips.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + offset), 250);
    return () => clearInterval(t);
  }, [offset]);

  // Derive the LIVE phase locally (server state may be seconds stale).
  const launchAt = state?.launchAt ?? 0;
  const crashesAt = state?.crashesAt ?? null;
  const phase: "boarding" | "flying" | "landed" | null = !state
    ? null
    : now < launchAt
      ? "boarding"
      : crashesAt !== null && now < crashesAt
        ? "flying"
        : "landed";

  // Poll: every 10s while boarding/landed (round rollover + boarded count), plus an
  // immediate fetch right at launch (to pull crashPoint/crashesAt).
  useEffect(() => {
    if (!phase) return;
    if (phase === "flying") return; // curve is client-side; nothing to poll
    const t = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(t);
  }, [phase, refresh]);
  const fetchedForLaunch = useRef(0);
  useEffect(() => {
    if (!state || phase !== "boarding") return;
    const ms = launchAt - now;
    if (ms <= 0 || fetchedForLaunch.current === state.roundId) return;
    const t = setTimeout(() => {
      fetchedForLaunch.current = state.roundId;
      void refresh();
    }, ms + 150);
    return () => clearTimeout(t);
  }, [state, phase, launchAt, now, refresh]);

  // Crash moment: haptic + refetch for the post-mortem.
  const crashedFor = useRef(0);
  useEffect(() => {
    if (!state || phase !== "landed" || crashedFor.current === state.roundId) return;
    if (state.crashesAt === null) return;
    crashedFor.current = state.roundId;
    haptic.tooSoon();
    void refresh();
  }, [state, phase, refresh]);

  const doBoard = async () => {
    setError(null);
    try {
      const res = await fetch("/api/rocket/board", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ anonId: getAnonId(), handle: getHandle() }),
      });
      if (!res.ok) {
        setError("boarding failed — doors may have closed");
        return;
      }
      haptic.land();
      track("rocket", "play_start");
      void refresh();
    } catch {
      setError("network hiccup — try again");
    }
  };

  const onBoard = () => {
    if (hasHandle()) void doBoard();
    else setShowHandle(true);
  };

  const doCashout = async () => {
    if (!state || cashed !== null) return;
    try {
      const res = await fetch("/api/rocket/cashout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ anonId: getAnonId(), roundId: state.roundId }),
      });
      const data = (await res.json()) as {
        won?: boolean;
        multiplier?: number;
        value?: number;
        total?: number;
        percentile?: number;
        error?: string;
      };
      if (!res.ok || data.won === false) {
        setError(data.error ?? "too late — it blew");
        return;
      }
      if (data.multiplier !== undefined && data.value !== undefined) {
        haptic.newBest();
        setCashed(data.value);
        setCard(
          buildRocketCard(data.multiplier, data.value, getHandle() ?? "you", {
            total: data.total ?? 0,
            percentile: data.percentile ?? 100,
          }),
        );
        track("rocket", "play_done");
        void board.refresh();
      }
    } catch {
      setError("network hiccup — tap again");
    }
  };

  const myResult = cashed ?? (state ? state.myResult : null);

  // Desktop nicety: space/enter cashes out mid-flight. Buttons and inputs keep
  // their native behavior (excluded targets) so nothing double-fires.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== "Enter") return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "BUTTON")
      )
        return;
      if (phase !== "flying" || !state?.meBoarded || showHandle) return;
      if (myResult !== null) return;
      e.preventDefault();
      void doCashout();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!state || !phase) {
    return (
      <div className="flex w-full max-w-md flex-col items-center gap-4 lg:min-w-0 lg:max-w-2xl lg:justify-self-center">
        <p className="animate-fade-up text-sm text-text-faint">
          finding the launch pad…
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-4 lg:min-w-0 lg:max-w-2xl lg:justify-self-center"
      style={{ animationDelay: "80ms" }}
    >
      {phase === "boarding" && (
        <Boarding
          countdown={fmtMMSS(launchAt - now)}
          boardedCount={state.boardedCount}
          meBoarded={state.meBoarded}
          onBoard={onBoard}
          last={state.last}
        />
      )}

      {phase === "flying" && (
        <Flight
          offset={offset}
          launchAt={launchAt}
          crashPoint={state.crashPoint ?? 1000}
          meBoarded={state.meBoarded}
          cashedValue={myResult}
          onCashout={() => void doCashout()}
        />
      )}

      {phase === "landed" && (
        <Landed
          state={state}
          myResult={myResult}
          nextBoardingMs={(Math.floor(now / 600_000) + 1) * 600_000 - now}
        />
      )}

      {error && <p className="text-sm text-accent">{error}</p>}

      {card && (
        <ShareSheet
          card={card}
          replyLink="rocket.karrtopelka.com/r"
          caption={`${card.display} off the shared rocket. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      )}

      {/* Mobile-only summary board — the desktop rail owns this above lg. */}
      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          best shared-flight cashouts
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => `${fromScoreValue(v)}x`}
          emptyLabel="no survivors yet — next flight in 10."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doBoard();
          }}
        />
      )}
    </div>
  );
}

function Boarding({
  countdown,
  boardedCount,
  meBoarded,
  onBoard,
  last,
}: {
  countdown: string;
  boardedCount: number;
  meBoarded: boolean;
  onBoard: () => void;
  last: RocketState["last"];
}) {
  return (
    <>
      <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-lg border border-border bg-surface lg:aspect-[16/10]">
        <span className="animate-float text-5xl">🚀</span>
        <span className="font-display text-6xl font-extrabold tabular-nums text-text lg:text-7xl">
          {countdown}
        </span>
        <span className="text-xs uppercase tracking-widest text-text-faint">
          until launch · <span className="text-accent">{boardedCount} aboard</span>
        </span>
        {last && (
          <span className="mt-2 text-xs text-text-faint">
            last flight blew at{" "}
            <span className="font-mono text-danger">{last.crashPoint.toFixed(2)}x</span>
            {" · "}
            {last.survivors} of {last.survivors + last.riders} got off
            {last.best > 0 && <> · best {fromScoreValue(last.best)}x</>}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onBoard}
        disabled={meBoarded}
        className={`w-full rounded-md px-4 py-4 text-lg font-bold transition-all duration-200 active:scale-[0.99] ${
          meBoarded
            ? "border border-win/40 text-win"
            : "animate-neon-pulse bg-accent text-bg hover:-translate-y-0.5 hover:shadow-glow"
        }`}
      >
        {meBoarded ? "✓ boarded — don't miss the launch" : "BOARD THIS FLIGHT"}
      </button>
    </>
  );
}

/** Multiplier gridline candidates — classic crash-chart furniture. */
const GRID_LEVELS = [1.5, 2, 3, 5, 10, 20, 50, 100, 250, 500];

/** The shared climb — same formula on every screen, anchored to launchAt. */
function Flight({
  offset,
  launchAt,
  crashPoint,
  meBoarded,
  cashedValue,
  onCashout,
}: {
  /** Server-clock correction; the curve runs off Date.now() + offset. */
  offset: number;
  launchAt: number;
  crashPoint: number;
  meBoarded: boolean;
  cashedValue: number | null;
  onCashout: () => void;
}) {
  // Smooth rAF clock for the curve (the page-level 250ms tick is too chunky).
  const [, force] = useState(0);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      force((n) => n + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const elapsed = Math.max(0, (Date.now() + offset - launchAt) / 1000);
  const mult = Math.min(multiplierAt(elapsed), crashPoint);

  const tMax = Math.max(elapsed * 1.12, 5);
  const yMax = Math.max(mult * 1.3, 2);
  const X = (t: number) => (t / tMax) * 100;
  const Y = (m: number) => 96 - ((m - 1) / (yMax - 1)) * 90;
  let line = `M 0 ${Y(1).toFixed(2)}`;
  for (let i = 1; i <= 120; i++) {
    const t = (elapsed * i) / 120;
    line += ` L ${X(t).toFixed(2)} ${Y(multiplierAt(t)).toFixed(2)}`;
  }
  const area = `${line} L ${X(elapsed).toFixed(2)} 100 L 0 100 Z`;
  const lockedMult = cashedValue !== null ? cashedValue / 100 : null;

  // Faint pink altitude lines — the last few that fit under the current ceiling.
  const grid = GRID_LEVELS.filter((m) => m < yMax * 0.97).slice(-4);
  const tipColor = lockedMult !== null ? colors.gold : colors.win;
  const glow = lockedMult !== null ? "rgba(255,212,94,0.4)" : "rgba(0,232,123,0.4)";

  return (
    <>
      <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-border bg-surface lg:aspect-[16/10]">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="rocket-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.win} stopOpacity="0.28" />
              <stop offset="100%" stopColor={colors.win} stopOpacity="0" />
            </linearGradient>
          </defs>
          {grid.map((m) => (
            <line
              key={m}
              x1="0"
              x2="100"
              y1={Y(m)}
              y2={Y(m)}
              stroke={colors.accent}
              strokeOpacity="0.12"
              strokeWidth="1"
              strokeDasharray="1 3"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {lockedMult !== null && (
            <line
              x1="0"
              x2="100"
              y1={Y(lockedMult)}
              y2={Y(lockedMult)}
              stroke={colors.gold}
              strokeWidth="1.5"
              strokeDasharray="2 2"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <path d={area} fill="url(#rocket-area)" />
          <path
            d={line}
            fill="none"
            stroke={tipColor}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {/* altitude labels — HTML so they never stretch with the viewBox */}
        {grid.map((m) => (
          <span
            key={m}
            className="pointer-events-none absolute right-2 -translate-y-1/2 font-mono text-[10px] text-accent/40"
            style={{ top: `${Y(m)}%` }}
          >
            {m}x
          </span>
        ))}
        {/* rocket tip — HTML dot so it stays round in any aspect ratio */}
        <div
          className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: `${X(elapsed)}%`,
            top: `${Y(mult)}%`,
            backgroundColor: tipColor,
            boxShadow: `0 0 14px ${tipColor}`,
          }}
        />
        <div className="pointer-events-none relative flex h-full flex-col items-center justify-center">
          <span
            className="font-display text-7xl font-extrabold tabular-nums text-text lg:text-8xl"
            style={{ textShadow: `0 0 ${Math.min(48, 10 + mult * 4).toFixed(0)}px ${glow}` }}
          >
            {mult.toFixed(2)}x
          </span>
          <span className="mt-2 text-sm text-text-faint lg:text-base">
            {meBoarded
              ? lockedMult !== null
                ? `locked ${lockedMult.toFixed(2)}x · watching the wreck`
                : "everyone's on THIS rocket"
              : "spectating — board the next one"}
          </span>
        </div>
      </div>
      {meBoarded && (
        <button
          type="button"
          onClick={onCashout}
          disabled={cashedValue !== null}
          className={`w-full rounded-md px-4 py-4 text-lg font-bold transition-all duration-200 active:scale-[0.98] ${
            cashedValue !== null
              ? "bg-gold text-bg shadow-glow-gold"
              : "bg-win text-bg shadow-glow-win hover:-translate-y-0.5 hover:brightness-110"
          }`}
        >
          {cashedValue !== null
            ? `locked ${(cashedValue / 100).toFixed(2)}x ✓`
            : `CASH OUT · ${mult.toFixed(2)}x`}
        </button>
      )}
    </>
  );
}

function Landed({
  state,
  myResult,
  nextBoardingMs,
}: {
  state: RocketState;
  myResult: number | null;
  nextBoardingMs: number;
}) {
  const cp = state.crashPoint;
  return (
    <>
      <div className="relative flex aspect-square w-full animate-flash-bust flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-danger/40 bg-surface shadow-glow-danger lg:aspect-[16/10]">
        {/* detonation ring — one beat, then gone */}
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 -ml-12 -mt-12 h-24 w-24 animate-burst rounded-full border-2 border-danger"
          aria-hidden
        />
        <span className="animate-shake text-sm font-bold uppercase tracking-wide text-danger">
          💥 flight over
        </span>
        {cp !== null && (
          <span className="animate-pop font-display text-6xl font-extrabold text-danger lg:text-7xl">
            {cp.toFixed(2)}x
          </span>
        )}
        {state.meBoarded ? (
          myResult !== null && myResult > 0 ? (
            <span className="text-sm font-semibold text-win">
              you got off at {(myResult / 100).toFixed(2)}x 🎯
            </span>
          ) : (
            <span className="text-sm text-text-muted">
              you rode it all the way down
            </span>
          )
        ) : (
          <span className="text-sm text-text-faint">you watched from the ground</span>
        )}
        <span className="mt-2 font-display text-2xl font-bold tabular-nums text-text lg:text-3xl">
          {fmtMMSS(nextBoardingMs)}
        </span>
        <span className="text-xs uppercase tracking-widest text-text-faint">
          until next boarding
        </span>
      </div>
    </>
  );
}
